import { Box, Button, FormControlLabel, FormGroup, Menu, Radio, Typography } from '@mui/material';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Filter, FilterOption, FiltersHandlers } from '../../filter-types';
import { ReactComponent as ArrowUp } from '../../../../images/arrow-up.svg';
import { ReactComponent as ArrowDown } from '../../../../images/arrow-down.svg';
import { i18n } from '../../../../i18n/i18n';
import { COLORS } from '../../../../constants/constants';
import { usePopup } from '@cellebrite/saas-ui-common';

interface SingleSelectionFilterProps {
    filter: Filter;
    onApply: (filterName: string, selectedValue: FilterOption | null) => void;
}

const SingleSelectionFilter = forwardRef<FiltersHandlers, SingleSelectionFilterProps>(
    ({ filter, onApply }, forwardedRef) => {
        const [selectedOption, setSelectedOption] = useState<FilterOption | null>(null);
        const [chosenOption, setChosenOption] = useState<FilterOption | null>(null);
        const [open, anchorEl, handleClick, handleClose] = usePopup(null, () =>
            setTimeout(() => {
                setSelectedOption(null);
            }, 300),
        );

        useImperativeHandle(forwardedRef, () => ({
            updateChosenSelection: (options: FilterOption | FilterOption[] | null) =>
                setChosenOption(options as FilterOption | null),
            resetChosenSelection: () => setChosenOption(null),
            removeChosenSelectionByValue: (value) => setChosenOption(null),
        }));

        return (
            <>
                <Button
                    color="tetriary"
                    sx={{
                        backgroundColor: chosenOption || open ? COLORS.BRAND_LIGHTEST : null,
                    }}
                    onClick={(e) => {
                        setSelectedOption(chosenOption);
                        handleClick(e);
                    }}
                >
                    <Typography
                        className={chosenOption || open ? 'color-b' : 'color-bd'}
                        sx={{
                            mr: '14px',
                            fontSize: '16px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {filter.name}
                    </Typography>
                    {open ? (
                        <ArrowUp className="fill-b" />
                    ) : (
                        <ArrowDown className={chosenOption ? 'fill-b' : 'fill-bd'} />
                    )}
                </Button>
                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <Box sx={{ p: 1, minWidth: 256 }}>
                        <FormGroup sx={{ maxHeight: 300, pr: 1 }}>
                            {filter.options.map((option, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <FormControlLabel
                                        sx={{
                                            height: '32px',
                                            mx: 0,
                                        }}
                                        value={option.value}
                                        control={<Radio />}
                                        label={option.label}
                                        checked={option.value === selectedOption?.value}
                                        onChange={() => setSelectedOption(option)}
                                    />
                                    {option.count && <Typography>{option.count}</Typography>}
                                </Box>
                            ))}
                        </FormGroup>

                        <Box sx={{ float: 'right', pb: 1, pr: 1, pt: 3 }} className="background-color-l">
                            <Button
                                sx={{ height: 32 }}
                                color="secondary"
                                onClick={() => {
                                    onApply(filter.name, null);
                                    setChosenOption(null);
                                    handleClose();
                                }}
                            >
                                {i18n.translate('clear')}
                            </Button>
                            <Button
                                sx={{ height: 32, ml: 1 }}
                                onClick={() => {
                                    onApply(filter.originalName, selectedOption);
                                    setChosenOption(selectedOption);
                                    handleClose();
                                }}
                                disabled={!selectedOption}
                            >
                                {i18n.translate('Apply')}
                            </Button>
                        </Box>
                    </Box>
                </Menu>
            </>
        );
    },
);

export default SingleSelectionFilter;
